import { ESdChangeReason, ICommentDto, LeftPanelType, ViewPart, Visibility } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import React from "react";
import { IComments } from "../../../models/interface/comment";
import { CommentService } from "../../../services/comment-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { getOpenCount, setOpenCount, subscribeOpenCount } from "../left-panel/comments-panel/comment-count-store";
import { convertCommentsDtoToInterface } from "../left-panel/comments-panel/comments-panel-logic";
import { isLeftPanelButtonActive } from "./left-panel-rail";

export interface ICommentsButtonState {
  openCount: number;
}

export interface ICommentsButtonProps {
  onMenuItemClick?: (panelType: LeftPanelType) => void;
}

class CommentsButton extends PureSdComponent<ICommentsButtonProps & IWithSDProps, ICommentsButtonState> {
  private unsubscribeOpenCount: (() => void) | null = null;
  // Guards out-of-order count fetches when the screen changes rapidly.
  private fetchSeq: number = 0;

  constructor(props: ICommentsButtonProps & IWithSDProps) {
    super(props);
    this.state = { openCount: getOpenCount() };
  }

  componentDidMount(): void {
    this.unsubscribeOpenCount = subscribeOpenCount((): void => {
      this.setState({ openCount: getOpenCount() });
    });
    void this.refreshCountIfPanelClosed();
  }

  componentDidUpdate(prevProps: ICommentsButtonProps & IWithSDProps): void {
    if (this.props.sd?.sdId && this.props.sd.sdId !== prevProps.sd?.sdId) {
      void this.refreshCountIfPanelClosed();
    }
  }

  componentWillUnmount(): void {
    if (this.unsubscribeOpenCount) this.unsubscribeOpenCount();
  }

  // The comments panel refreshes the rail badge while it's open; when it's closed it isn't even
  // mounted, so on load / screen / project change the badge would otherwise stay stale. Fetch the
  // new screen's open count here in that case.
  private refreshCountIfPanelClosed = async (): Promise<void> => {
    if (this.props.sd.getActiveLeftPanel() === LeftPanelType.Comments) return;
    const sdId: string | undefined = this.props.sd?.sdId;
    // On first app mount (and non-editor routes) sdId is a non-GUID placeholder before a real
    // screen loads; the /comments endpoint takes a `Guid`, so calling it then 400s. Only fetch
    // once a real screen is loaded — componentDidUpdate re-runs this when sdId becomes a GUID.
    if (!sdId || !Guid.isGuid(sdId)) return;
    const seq: number = ++this.fetchSeq;
    const res = await CommentService.GetAllByScreenDesignId(sdId);
    if (seq !== this.fetchSeq) return;
    if (res && res.status === 200) {
      const threads: IComments[] = convertCommentsDtoToInterface(res.data as ICommentDto[]);
      setOpenCount(threads.filter((t: IComments) => !t.comments[0]?.isResolved).length);
    } else {
      setOpenCount(0);
    }
  };

  handleClick = (): void => {
    if (this.props.onMenuItemClick) {
      this.props.onMenuItemClick(LeftPanelType.Comments);
    } else {
      this.props.sd.setActiveLeftPanelByClick(LeftPanelType.Comments);
    }
  };

  render(): React.ReactNode {
    const { openCount } = this.state;
    if (this.props.sd.getVisibility(ViewPart.CommentsButton) !== Visibility.Visible) {
      return null;
    }
    return (
      // Badge is a SIBLING of the button (not a child): KuikaButton forces child text to
      // --dark-7 via !important, which an inline color can't beat. Kept outside its cascade.
      <div style={{ position: "relative", display: "inline-flex" }}>
        <KuikaButton
          data-testid="leftMenuCommentsButton"
          type="tertiary"
          size="md"
          isIcon
          color="neutral"
          active={isLeftPanelButtonActive(this.props.sd, LeftPanelType.Comments)}
          onClick={this.handleClick}
        >
          {/* The rail's standard icon call — KuikaSvg + a set asset, like every sibling button.
              This was a hand-rolled inline path at strokeWidth 1.8, which is why it read heavier
              than its neighbours. */}
          <KuikaSvg name="message" width={18} height={18} pointer />
        </KuikaButton>
        {openCount > 0 && (
          <span
            data-testid="leftMenuCommentsBadge"
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              minWidth: 16,
              height: 16,
              borderRadius: "var(--radius-pill)",
              background: "var(--blue-5)",
              color: "var(--white)",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 4px",
              boxSizing: "border-box",
              // The ring punches the badge out of the rail behind it, so it has to BE the rail:
              // `.kuika-left-menu` is --dark-0. --dark-1 drew a lighter grey halo in dark mode.
              boxShadow: "0 0 0 2px var(--dark-0)",
              pointerEvents: "none",
              zIndex: 1
            }}
          >
            {openCount > 99 ? "99+" : openCount}
          </span>
        )}
      </div>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const commentsButton = withSD<ICommentsButtonProps>(CommentsButton);
export { commentsButton as CommentsButton };
